import React, { useMemo, useState } from 'react';
import {
    getRecipientSummary,
    RECIPIENT_CHIP_LIMIT,
    shouldCollapseRecipients,
} from '../../../helpers/messagingRecipients.ts';

interface RecipientsPanelProps {
    recipients: string[];
    groupLabel?: string;
    onRemove?: (userName: string) => void;
    onClearAll?: () => void;
    readOnly?: boolean;
    defaultExpanded?: boolean;
}

const RecipientsPanel = ({
    recipients,
    groupLabel,
    onRemove,
    onClearAll,
    readOnly = false,
    defaultExpanded = false,
}: RecipientsPanelProps) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [filter, setFilter] = useState('');

    const collapsed = shouldCollapseRecipients(recipients, groupLabel);
    const summary = getRecipientSummary(recipients, groupLabel);

    const filteredRecipients = useMemo(() => {
        const query = filter.trim().toLowerCase();
        if (!query) return recipients;
        return recipients.filter(name => name.toLowerCase().includes(query));
    }, [recipients, filter]);

    if (recipients.length === 0) return null;

    if (!collapsed) {
        return (
            <div className="recipients-panel">
                <div className="recipients-panel-header">
                    <span className="selected-users-count">
                        {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} selected
                    </span>
                    {!readOnly && onClearAll && recipients.length > 1 && (
                        <button type="button" className="recipients-clear-btn" onClick={onClearAll}>
                            Clear all
                        </button>
                    )}
                </div>
                <div className="selected-users">
                    {recipients.map((userName) => (
                        <span key={userName} className="array-user">
                            {userName}
                            {!readOnly && onRemove && (
                                <button
                                    type="button"
                                    className="remove-user"
                                    onClick={() => onRemove(userName)}
                                >
                                    ×
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="recipients-panel recipients-panel--collapsed">
            <div className="recipients-summary-card">
                <div className="recipients-summary-info">
                    <span className="recipients-summary-label">{summary}</span>
                    {groupLabel && (
                        <span className="recipients-summary-hint">Bulk group selected</span>
                    )}
                </div>
                <div className="recipients-summary-actions">
                    <button
                        type="button"
                        className="recipients-toggle-btn"
                        onClick={() => setExpanded(prev => !prev)}
                    >
                        {expanded ? 'Hide list' : 'View list'}
                    </button>
                    {!readOnly && onClearAll && (
                        <button type="button" className="recipients-clear-btn" onClick={onClearAll}>
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="recipients-expanded-list">
                    {recipients.length > RECIPIENT_CHIP_LIMIT && (
                        <input
                            type="text"
                            className="recipients-filter-input"
                            placeholder="Filter recipients..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    )}
                    <div className="recipients-scroll-list">
                        {filteredRecipients.length === 0 ? (
                            <span className="recipients-no-match">No matches found</span>
                        ) : (
                            filteredRecipients.map((userName) => (
                                <div key={userName} className="recipients-list-item">
                                    <span>{userName}</span>
                                    {!readOnly && onRemove && !groupLabel && (
                                        <button
                                            type="button"
                                            className="remove-user"
                                            onClick={() => onRemove(userName)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    <span className="recipients-list-footer">
                        Showing {filteredRecipients.length} of {recipients.length}
                    </span>
                </div>
            )}
        </div>
    );
};

export default RecipientsPanel;
